'use client'

import Image from 'next/image'
import React, { useEffect, useRef } from 'react'

import { cn } from '@/utils/twMerge'

import type { MegaKey } from '@/components/navbar/megaKeys'

type NavbarDropdownItemProps = {
  className: string
  label: string
  megaKey: MegaKey
  isOpen: boolean
  menuId: string
  onOpen: (key: MegaKey) => void
  onRequestClose: () => void
  onDismiss: () => void
  /** Move focus into the shared mega tray (Tab from open trigger). */
  onEnterMenu: () => void
}

const NavbarDropdownItem = ({
  className,
  label,
  megaKey,
  isOpen,
  menuId,
  onOpen,
  onRequestClose,
  onDismiss,
  onEnterMenu,
}: NavbarDropdownItemProps) => {
  const itemRef = useRef<HTMLLIElement>(null)

  useEffect(() => {
    const element = itemRef.current
    if (!element) return

    const onFocusIn = () => onOpen(megaKey)
    const onFocusOut = (event: FocusEvent) => {
      const next = event.relatedTarget as Node | null
      if (next && element.contains(next)) return
      // Keep open while focus moves into the shared tray (or another dropdown trigger)
      if (next instanceof Element && next.closest('.mega-tray, .dropdown')) return
      onRequestClose()
    }

    element.addEventListener('focusin', onFocusIn)
    element.addEventListener('focusout', onFocusOut)
    return () => {
      element.removeEventListener('focusin', onFocusIn)
      element.removeEventListener('focusout', onFocusOut)
    }
  }, [megaKey, onOpen, onRequestClose])

  return (
    <li
      ref={itemRef}
      className={cn('dropdown', className, isOpen && 'is-open')}
      onMouseEnter={() => onOpen(megaKey)}
      onMouseLeave={onRequestClose}>
      <button
        type="button"
        className="navbar-nav-link bg-transparent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-green-150 focus-visible:ring-offset-2"
        aria-expanded={isOpen}
        aria-haspopup="true"
        aria-controls={menuId}
        onClick={() => (isOpen ? onDismiss() : onOpen(megaKey))}
        onKeyDown={(event) => {
          if (!isOpen) return
          // Tab from open trigger enters tray before later nav items (DOM tray is after actions)
          if (event.key === 'Tab' && !event.shiftKey) {
            event.preventDefault()
            onEnterMenu()
          }
        }}>
        <span className="block text-base font-normal">{label}</span>
        <Image src="/images/angle-down.svg" width={7} height={7} alt="" aria-hidden="true" />
      </button>
    </li>
  )
}

export default NavbarDropdownItem
